import * as React from 'react';
import { Popover, PopoverAnchor, PopoverContent } from '@ooh/ui/components/popover';
import { Button } from '@ooh/ui/components/button';
import type { CellState, AvailabilityPeriod, CellSelection } from '../lib/availability-types';
import { formatTtlRemaining, useTtlCountdown } from '../lib/ttl-countdown';
import type { CellEntry } from '@ooh/contracts';
import type { CellPopoverLabels } from './cell-popover-state';
import { getCellPopoverState } from './cell-popover-state';

// ─── CellPopover ──────────────────────────────────────────────

interface CellPopoverProps {
  selection: CellSelection;
  periods: AvailabilityPeriod[];
  unitLabel: string;
  labels: CellPopoverLabels;
  getCellStateForPeriod: (periodId: string) => CellState;
  getReservationStatusForPeriod?: (
    periodId: string,
  ) => 'SOFT_RESERVATION' | 'HARD_RESERVATION' | null;
  /**
   * Entries for the selected cell — supplied by the inventory / detail view when
   * the entriesGrid is available. When present and the cell is in 'occupied' mode
   * (SOFT/HARD/OPTION held by another user), holder and client names are rendered
   * below the static occupiedByOther label so the operator sees who holds the slot.
   */
  entries?: CellEntry[];
  /** Anchor element — popover positions itself below this element. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** TTL expiry date for own-reservation cells; null/undefined hides the countdown. */
  ttlExpiresAt?: Date | null;
  /** Called once when the countdown first reaches zero — use to trigger a data refetch. */
  onExpired?: () => void;
  onClose: () => void;
  onReserve?: (selection: CellSelection, status: 'SOFT_RESERVATION' | 'OPTION') => void;
  onUpgradeHard?: (selection: CellSelection) => void;
  onCloseSale?: (selection: CellSelection) => void;
  onRelease?: (selection: CellSelection) => void;
  /**
   * Queue info for cells held by another user. When provided alongside showCloseAction,
   * renders the waitlist affordance: count label, join or leave button.
   * myPosition === null means the current user is NOT in the queue.
   */
  queueInfo?: { count: number; myPosition: number | null };
  /** Called when the user clicks "Join queue". */
  onJoinQueue?: (selection: CellSelection) => void;
  /** Called when the user clicks "Leave queue". */
  onLeaveQueue?: (selection: CellSelection) => void;
}

function CellPopoverActions({
  labels,
  selection,
  showReserveActions,
  showReleaseActions,
  showUpgradeHardAction,
  showCloseSaleAction,
  showCloseAction,
  onClose,
  onReserve,
  onUpgradeHard,
  onCloseSale,
  onRelease,
}: {
  labels: CellPopoverLabels;
  selection: CellSelection;
  showReserveActions: boolean;
  showReleaseActions: boolean;
  showUpgradeHardAction: boolean;
  showCloseSaleAction: boolean;
  showCloseAction: boolean;
  onClose: () => void;
  onReserve?: (selection: CellSelection, status: 'SOFT_RESERVATION' | 'OPTION') => void;
  onUpgradeHard?: (selection: CellSelection) => void;
  onCloseSale?: (selection: CellSelection) => void;
  onRelease?: (selection: CellSelection) => void;
}) {
  if (showReserveActions) {
    return (
      <div className="flex gap-2 pt-1">
        <Button
          size="xs"
          variant="outline"
          fullWidth
          onClick={() => onReserve?.(selection, 'OPTION')}
        >
          {labels.addOptionBtn}
        </Button>
        <Button size="xs" fullWidth onClick={() => onReserve?.(selection, 'SOFT_RESERVATION')}>
          {labels.reserveBtn}
        </Button>
      </div>
    );
  }

  if (showReleaseActions) {
    return (
      <div className="grid grid-cols-2 gap-2 pt-1">
        {showUpgradeHardAction ? (
          <Button size="xs" variant="outline" fullWidth onClick={() => onUpgradeHard?.(selection)}>
            {labels.upgradeHardBtn ?? labels.reserveBtn}
          </Button>
        ) : null}
        {showCloseSaleAction ? (
          <Button size="xs" variant="success" fullWidth onClick={() => onCloseSale?.(selection)}>
            {labels.closeSaleBtn ?? labels.reserveBtn}
          </Button>
        ) : null}
        <Button size="xs" variant="destructive" fullWidth onClick={() => onRelease?.(selection)}>
          {labels.releaseBtn}
        </Button>
        <Button size="xs" variant="ghost" fullWidth onClick={onClose}>
          {labels.cancelBtn}
        </Button>
      </div>
    );
  }

  if (showCloseAction) {
    return (
      <Button size="xs" variant="ghost" fullWidth onClick={onClose}>
        {labels.closeBtn}
      </Button>
    );
  }

  return null;
}

function CellQueueBlock({
  labels,
  selection,
  queueInfo,
  onJoinQueue,
  onLeaveQueue,
}: {
  labels: CellPopoverLabels;
  selection: CellSelection;
  queueInfo: { count: number; myPosition: number | null };
  onJoinQueue?: (selection: CellSelection) => void;
  onLeaveQueue?: (selection: CellSelection) => void;
}) {
  return (
    <div className="space-y-1.5 pt-1 border-t border-border">
      {labels.queueCountLabel != null ? (
        <p className="text-xs text-muted-foreground">{labels.queueCountLabel(queueInfo.count)}</p>
      ) : null}
      {queueInfo.myPosition === null ? (
        <Button
          size="xs"
          variant="outline"
          fullWidth
          data-testid="cell-queue-join"
          onClick={() => onJoinQueue?.(selection)}
        >
          {labels.queueJoinBtn}
        </Button>
      ) : (
        <div className="space-y-1.5">
          {labels.queueMyPositionLabel != null ? (
            <p data-testid="cell-queue-position" className="text-xs font-medium text-foreground">
              {labels.queueMyPositionLabel(queueInfo.myPosition)}
            </p>
          ) : null}
          <Button
            size="xs"
            variant="ghost"
            fullWidth
            data-testid="cell-queue-leave"
            onClick={() => onLeaveQueue?.(selection)}
          >
            {labels.queueLeaveBtn}
          </Button>
        </div>
      )}
    </div>
  );
}

export function CellPopover({
  selection,
  periods,
  unitLabel,
  labels,
  getCellStateForPeriod,
  getReservationStatusForPeriod,
  entries,
  anchorRef,
  ttlExpiresAt,
  onExpired,
  onClose,
  onReserve,
  onUpgradeHard,
  onCloseSale,
  onRelease,
  queueInfo,
  onJoinQueue,
  onLeaveQueue,
}: CellPopoverProps) {
  // Track the anchor element's position so we can place a hidden PopoverAnchor
  // at the same location. Radix Floating UI then handles stacking context,
  // a11y attributes (role=dialog, aria-expanded), and animation tokens.
  const [anchorStyle, setAnchorStyle] = React.useState<React.CSSProperties>({});

  React.useLayoutEffect(() => {
    const updatePosition = () => {
      if (!anchorRef?.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setAnchorStyle({
        position: 'fixed',
        top: rect.bottom,
        left: Math.max(8, rect.left),
        width: rect.width,
        height: 0,
        pointerEvents: 'none',
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  const { autoSnap } = selection;
  const popoverState = getCellPopoverState(
    selection,
    periods,
    unitLabel,
    labels,
    getCellStateForPeriod,
    getReservationStatusForPeriod,
  );

  const remaining = useTtlCountdown(ttlExpiresAt, onExpired);

  return (
    <Popover open={true}>
      {/*
       * A zero-height fixed span is placed at the bottom edge of the anchor cell.
       * Radix reads its position to float the PopoverContent below it, giving us
       * Radix stacking context, a11y attributes, and animation tokens without
       * ReactDOM.createPortal or hard-coded zIndex.
       */}
      <PopoverAnchor asChild>
        <span style={anchorStyle} />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={6}
        onPointerDownOutside={onClose}
        onEscapeKeyDown={onClose}
        className="p-3 space-y-2 text-[13px] whitespace-normal"
        onClick={(e) => e.stopPropagation()}
        data-testid="cell-popover"
      >
        {autoSnap !== undefined ? (
          <p className="text-2xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 leading-snug">
            {labels.autoSnapWarning(autoSnap.mediaTypeLabel, autoSnap.snappedLabel)}
          </p>
        ) : null}

        <p className="font-medium text-foreground leading-snug">{popoverState.message}</p>

        {/* When the cell is occupied by another holder and we have entry details, render them. */}
        {popoverState.showCloseAction && entries != null && entries.length > 0 ? (
          <div className="space-y-0.5 text-xs text-muted-foreground">
            {entries.map((entry, idx) => {
              const holderParts: string[] = [];
              if (entry.holderName) holderParts.push(entry.holderName);
              if (entry.clientName) holderParts.push(entry.clientName);
              const label = holderParts.join(' · ') || entry.status;
              return (
                <p key={entry.conflictingOfferId ?? idx} data-testid={`popover-entry-${idx}`}>
                  {label}
                </p>
              );
            })}
          </div>
        ) : null}

        {remaining !== null ? (
          <p
            data-testid="reservation-ttl-countdown"
            className="text-xs text-muted-foreground tabular-nums"
          >
            {labels.ttlCountdownLabel} {formatTtlRemaining(remaining)}
          </p>
        ) : null}

        {popoverState.showCloseAction && queueInfo != null ? (
          <CellQueueBlock
            labels={labels}
            selection={selection}
            queueInfo={queueInfo}
            onJoinQueue={onJoinQueue}
            onLeaveQueue={onLeaveQueue}
          />
        ) : null}

        <CellPopoverActions
          labels={labels}
          selection={selection}
          showReserveActions={popoverState.showReserveActions}
          showReleaseActions={popoverState.showReleaseActions}
          showUpgradeHardAction={popoverState.showUpgradeHardAction}
          showCloseSaleAction={popoverState.showCloseSaleAction}
          showCloseAction={popoverState.showCloseAction}
          onClose={onClose}
          onReserve={onReserve}
          onUpgradeHard={onUpgradeHard}
          onCloseSale={onCloseSale}
          onRelease={onRelease}
        />
      </PopoverContent>
    </Popover>
  );
}
